import { AccessibilityInfo, BackHandler, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Tabs, usePathname, useRouter } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { PlanoBasicoVencido } from '../../screens/Paywall';
import type { ComponentProps } from 'react';
import { IconeAba } from '../../components/IconeAba';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTaskSheet, diaAberto } from '../../components/TaskSheet';
import { novoAtual, useNovoAtual } from '../../lib/botaoNovo';
import { useFaixaSelecao } from '../../lib/faixaSelecao';
import { C, aba, alpha, useCores } from '../../components/theme';
import { Listras } from '../../components/TemaPrevia';
import { setDoc, useDoc } from '../../store/store';
import { barraDe } from '../../store/barra';
import { N_, t } from '../../lib/i18n';
import { naoLidasTotal, useChat } from '../../lib/chat';
import { tomarIrAjustes } from '../../lib/retomar';
import { marcarVoltarAndroid } from '../../lib/voltarPlanner';
import { useAlvoGuia } from '../../lib/guia/destaque';
export { ErrorBoundary } from '../../components/Falha';

type BottomTabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];

const ITENS: Record<string, [string, string]> = {
  mensagens: ['💬', N_('Mensagens')], anotacoes: ['🗒️', N_('Anotações')], index: ['📅', N_('Planner')], areas: ['🧩', N_('Abas')], ajustes: ['⚙️', N_('Ajustes')],
};
const LARG = 68;

/** 11d G: ícone da aba na barra. A aberta fica maior e um pouco "saltada" (transform: não muda o tamanho da barra);
 *  as outras ficam com a cor cheia (sem transparência). */
const Icone = ({ id, icon, foco }: { id?: string; icon: string; foco: boolean }) => (
  <View style={foco ? { transform: [{ translateY: -3 }, { scale: 1.22 }] } : undefined}>
    <IconeAba id={id} icon={icon} tam={20} />
  </View>
);
/** 11d G: nome da aba (negrito na aberta; cinza escuro bem legível nas outras) e a bolinha na cor do tema embaixo.
 *  A bolinha ocupa o mesmo espaço em todas as abas, para a barra nunca mudar de altura. */
const Nome = ({ nome, foco, cor }: { nome: string; foco: boolean; cor: string }) => (<>
  <Text numberOfLines={1} allowFontScaling={false} style={{ fontSize: 11, lineHeight: 14, textAlign: 'center', color: foco ? C.text : C.sub, fontWeight: foco ? '800' : '500' }}>{nome}</Text>
  <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: foco ? cor : 'transparent' }} />
</>);

type Item = { key: string; icon: string; nome: string; foco: boolean; ir: () => void; id?: string };

function Barra({ state, navigation }: BottomTabBarProps) {
  const ins = useSafeAreaInsets();
  const { accent, liberada, listras } = useCores();
  // temas Orgulho (0.67): listras da bandeira só em detalhes, nunca atrás de texto
  const marca = (foco: boolean) => (listras && foco ? (
    <View pointerEvents="none" style={{ position: 'absolute', bottom: 0, left: (LARG - 24) / 2, width: 24, height: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Listras cores={listras} altura={3} />
    </View>
  ) : null);
  const sheet = useTaskSheet();
  const novo = useNovoAtual();
  // 11d F: aviso curto quando a tela não tem nada para adicionar (não bloqueia: some sozinho)
  const [semNada, setSemNada] = useState(false);
  const tempo = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (tempo.current) clearTimeout(tempo.current); }, []);
  // Guia "Como usar" (texto 08, C1): pontos do "▶ Mostrar na tela" (o ref não muda o visual)
  const alvoMais = useAlvoGuia('geral.botaoMais'), alvoAbas = useAlvoGuia('geral.barra.abas'), alvoAjustes = useAlvoGuia('geral.barra.ajustes');
  const atual = state.routes[state.index];
  const abaAtual = atual?.name === 'aba/[id]' ? (atual.params as { id?: string } | undefined)?.id : undefined;
  // 11d F: o + cria o que a tela aberta mostra; no My Planner, uma tarefa do dia aberto; sem nada aqui: apagadinho
  const noPlanner = atual?.name === 'index';
  const apagado = !noPlanner && !novo;
  const tocarMais = () => {
    if (noPlanner) return sheet.open({ date: diaAberto.atual });
    const n = novoAtual();   // lido na hora do toque (acompanha a sub-aba aberta)
    if (n) return n.fn();
    const msg = t('Nesta tela não há nada para adicionar.');
    setSemNada(true);
    if (tempo.current) clearTimeout(tempo.current);
    tempo.current = setTimeout(() => setSemNada(false), 2200);
    AccessibilityInfo.announceForAccessibility?.(msg);
  };
  const fixo = (name: string): Item => ({ key: name, icon: ITENS[name][0], nome: t(ITENS[name][1]), foco: atual?.name === name,
    ir: () => {
      // avisa a tela (ex.: Ajustes volta para o menu quando a aba é tocada de novo)
      const r = state.routes.find(x => x.name === name);
      if (r) navigation.emit({ type: 'tabPress', target: r.key, canPreventDefault: true } as any);
      navigation.navigate(name as never);
    } });
  const aj = useDoc('ajustes');
  // 0.45: a aba Escritório entra uma vez na barra de quem já tinha escolhido as abas (dá para tirar em Abas)
  useEffect(() => {
    if (aj.barra && !aj.barraEscritorio) setDoc('ajustes', d => ({ ...d, barraEscritorio: true, barra: d.barra?.includes('escritorio') ? d.barra : ['escritorio', ...(d.barra ?? [])] }));
  }, [aj.barra, aj.barraEscritorio]);
  // texto 27 (B1): o Portal do Cliente agora é parte do Escritório & Clientes: na barra, o Portal vira o Escritório
  useEffect(() => {
    if (aj.barra?.includes('portal' as any)) setDoc('ajustes', d => {
      const b = (d.barra ?? []) as string[];
      const sem = b.filter(x => x !== 'portal');
      return { ...d, barra: (sem.includes('escritorio') ? sem : b.map(x => (x === 'portal' ? 'escritorio' : x))) as any };
    });
  }, [aj.barra]);
  // 0.49: a aba Scanner PDF entra uma vez na barra, ao lado do Escritório
  useEffect(() => {
    if (aj.barra && aj.barraEscritorio && !aj.barraScanner) setDoc('ajustes', d => {
      const b = d.barra ?? [];
      if (b.includes('scanner')) return { ...d, barraScanner: true };
      const i = b.indexOf('escritorio');
      return { ...d, barraScanner: true, barra: i >= 0 ? [...b.slice(0, i + 1), 'scanner', ...b.slice(i + 1)] : ['scanner', ...b] };
    });
  }, [aj.barra, aj.barraEscritorio, aj.barraScanner]);
  // 0.50: a aba PDF Edit entra uma vez na barra, ao lado do Scanner PDF
  useEffect(() => {
    if (aj.barra && aj.barraScanner && !aj.barraPdf) setDoc('ajustes', d => {
      const b = d.barra ?? [];
      if (b.includes('pdfedit')) return { ...d, barraPdf: true };
      const i = b.indexOf('scanner');
      return { ...d, barraPdf: true, barra: i >= 0 ? [...b.slice(0, i + 1), 'pdfedit', ...b.slice(i + 1)] : ['pdfedit', ...b] };
    });
  }, [aj.barra, aj.barraScanner, aj.barraPdf]);
  // abas fixadas pelo usuário (organizador), na ordem escolhida; as que não estão liberadas ficam só na tela Abas
  const fixadas: Item[] = barraDe(aj).filter(id => liberada(id)).map(id => (id === 'anotacoes' ? fixo('anotacoes') : {
    key: 'aba-' + id, id, icon: aba(id).icon, nome: aba(id).curto, foco: abaAtual === id,
    ir: () => (navigation.navigate as any)('aba/[id]', { id, item: undefined }),
  }));
  const itens = [fixo('index'), ...fixadas, fixo('areas'), fixo('ajustes')];
  // No navegador, a roda do mouse não rola na horizontal: setas nas pontas e a roda passam a rolar a barra.
  const rolagem = useRef<ScrollView>(null);
  const chat = useChat();
  const naoLidas = naoLidasTotal(chat);
  const faixa = useFaixaSelecao();
  const msg = fixo('mensagens');
  const [larg, setLarg] = useState({ vis: 0, cont: 0, x: 0 });
  const sobra = larg.cont - larg.vis > 4;
  const rolar = (dx: number) => rolagem.current?.scrollTo({ x: Math.max(0, Math.min(larg.cont - larg.vis, larg.x + dx)), animated: true });
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const no = (rolagem.current as any)?.getScrollableNode?.() as HTMLElement | undefined;
    if (!no) return;
    const roda = (e: WheelEvent) => {
      if (no.scrollWidth <= no.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      no.scrollLeft += e.deltaY; e.preventDefault();
    };
    no.addEventListener('wheel', roda, { passive: false });
    return () => no.removeEventListener('wheel', roda);
  }, []);
  return (
    <View style={{ backgroundColor: C.card, borderTopWidth: listras ? 0 : 1, borderTopColor: C.line, paddingBottom: ins.bottom + 4, paddingTop: 20, flexDirection: 'row' }}>
      {!!listras && <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0 }}><Listras cores={listras} altura={3} /></View>}
      {/* Mensagens: o único botão fixo da barra, sempre na ponta esquerda (fora da rolagem). */}
      <Pressable key="mensagens" onPress={msg.ir} accessibilityRole="tab" accessibilityState={{ selected: msg.foco }}
        accessibilityLabel={naoLidas ? t('Mensagens') + ' (' + naoLidas + ')' : t('Mensagens')}
        style={{ width: LARG, alignItems: 'center', justifyContent: 'flex-start', gap: 2, paddingTop: 2, borderRightWidth: 1, borderRightColor: C.line }}>
        <View>
          <Icone icon={msg.icon} foco={msg.foco} />
          {naoLidas > 0 && (
            <View style={{ position: 'absolute', top: -4, right: -12, minWidth: 18, height: 18, borderRadius: 9, backgroundColor: '#EF4444', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 }}>
              <Text style={{ color: '#fff', fontSize: 10, fontWeight: '800' }}>{naoLidas > 99 ? '99+' : naoLidas}</Text>
            </View>
          )}
        </View>
        <Nome nome={msg.nome} foco={msg.foco} cor={accent} />
        {marca(msg.foco)}
      </Pressable>
      <View style={{ flex: 1 }}>
      <ScrollView ref={rolagem} horizontal showsHorizontalScrollIndicator={false} scrollEventThrottle={32}
        onLayout={e => { const vis = e.nativeEvent.layout.width; setLarg(l => ({ ...l, vis })); }}
        onContentSizeChange={cont => setLarg(l => ({ ...l, cont }))}
        onScroll={e => { const x = e.nativeEvent.contentOffset.x; setLarg(l => ({ ...l, x })); }}
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'space-around', paddingHorizontal: Platform.OS === 'web' && sobra ? 26 : 4 }}>
        {itens.map(it => (
          <Pressable key={it.key} onPress={it.ir} accessibilityRole="tab" accessibilityState={{ selected: it.foco }}
            ref={it.key === 'areas' ? alvoAbas.ref : it.key === 'ajustes' ? alvoAjustes.ref : undefined} collapsable={false}
            style={{ width: LARG, alignItems: 'center', justifyContent: 'flex-start', gap: 2, paddingTop: 2 }}>
            <Icone id={it.id} icon={it.icon} foco={it.foco} />
            <Nome nome={it.nome} foco={it.foco} cor={accent} />
            {marca(it.foco)}
          </Pressable>
        ))}
      </ScrollView>
      {Platform.OS === 'web' && sobra && larg.x > 4 && (
        <Pressable onPress={() => rolar(-larg.vis * 0.7)} accessibilityLabel={t('Voltar')}
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: C.card }}>
          <Text style={{ color: C.text, fontSize: 22 }}>‹</Text>
        </Pressable>
      )}
      {Platform.OS === 'web' && sobra && larg.x < larg.cont - larg.vis - 4 && (
        <Pressable onPress={() => rolar(larg.vis * 0.7)} accessibilityLabel={t('Mais abas')}
          style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: C.card }}>
          <Text style={{ color: C.text, fontSize: 22 }}>›</Text>
        </Pressable>
      )}
      </View>
      {/* Botão "+" fixo, flutuando acima da barra (fora da rolagem). */}
      <View pointerEvents="box-none" style={{ position: 'absolute', top: -32, left: 0, right: 0, alignItems: 'center', display: faixa ? 'none' : 'flex' }}>
        <Pressable ref={alvoMais.ref} collapsable={false} delayLongPress={450}
          accessibilityLabel={noPlanner ? t('Nova tarefa') : novo ? novo.rotulo : t('Nesta tela não há nada para adicionar.')}
          accessibilityState={{ disabled: apagado }}
          onPress={tocarMais}
          onLongPress={() => sheet.open({})}
          style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: apagado ? C.card : accent, alignItems: 'center', justifyContent: 'center', borderWidth: 4, borderColor: C.bg,
            elevation: apagado ? 2 : 8, shadowColor: '#000', shadowOpacity: apagado ? 0.12 : 0.35, shadowRadius: 8, shadowOffset: { width: 0, height: 3 } }}>
          {apagado ? (<>
            {/* apagadinho: a cor do tema bem clarinha, sem cor cheia (o botão não some, para a barra não mudar) */}
            <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 22, backgroundColor: alpha(accent, 0.16) }} />
            <Text style={{ color: alpha(accent, 0.55), fontSize: 28, fontWeight: '300', marginTop: -2 }}>+</Text>
          </>) : listras ? (<>
            <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 22, overflow: 'hidden' }}>
              <Listras cores={listras} vertical altura={44} />
            </View>
            <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: accent, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: '#fff', fontSize: 24, fontWeight: '300', marginTop: -2 }}>+</Text>
            </View>
          </>) : <Text style={{ color: '#fff', fontSize: 28, fontWeight: '300', marginTop: -2 }}>+</Text>}
        </Pressable>
      </View>
      {/* texto 29 (item 2, REGRAS 1.4): com itens selecionados, a faixa de ações fica fixa aqui, no lugar da barra */}
      {!!faixa && (
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: C.card, borderTopWidth: 2, borderTopColor: faixa.cor, paddingBottom: ins.bottom + 4, justifyContent: 'center' }}>
          {!!faixa.aviso && <Text style={{ color: C.sub, fontSize: 11, textAlign: 'center', marginBottom: 4 }}>{faixa.aviso}</Text>}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flexGrow: 0 }} contentContainerStyle={{ gap: 8, paddingHorizontal: 12, flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
            {faixa.acoes.map(a => (
              <Pressable key={a.label} onPress={a.onPress} accessibilityRole="button" accessibilityState={{ disabled: !!a.desativada }}
                style={{ paddingHorizontal: 14, paddingVertical: 10, borderRadius: 12, backgroundColor: a.cor ?? faixa.cor, opacity: a.desativada ? 0.5 : 1 }}>
                <Text style={{ color: '#fff', fontWeight: '800', fontSize: 13 }}>{a.label}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
      {semNada && (
        <View pointerEvents="none" style={{ position: 'absolute', top: -84, left: 16, right: 16, alignItems: 'center' }}>
          <View style={{ backgroundColor: C.card2, borderRadius: 14, paddingVertical: 10, paddingHorizontal: 14, borderWidth: 1, borderColor: C.line, maxWidth: 520,
            shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 }}>
            <Text style={{ color: C.text, textAlign: 'center' }}>{t('Nesta tela não há nada para adicionar.')}</Text>
          </View>
        </View>
      )}
    </View>
  );
}

export const unstable_settings = { initialRouteName: 'index' };
/** Já abriu o My Planner ao abrir o app (vale até o app ser fechado de verdade). */
let abriuDoZero = false;

export default function TabsLayout() {
  const router = useRouter();
  const caminho = usePathname();
  const { situacao } = useCores();

  // Texto 07 A4: o app SEMPRE abre no My Planner, no dia de hoje, mas só quando abre do zero (estava fechado).
  // Sair rapidinho e voltar não muda nada. A "Tela inicial" antiga (ajustes.telaInicial) não é mais usada.
  // Quando o app só é remontado (trocar modo, tema ou tamanho do texto), não volta ao início: se Ajustes pediu, volta a Ajustes.
  useEffect(() => {
    const t0 = setTimeout(() => {
      if (!abriuDoZero) { abriuDoZero = true; return router.navigate('/'); }
      if (tomarIrAjustes()) router.navigate('/ajustes');
    }, 0);
    return () => clearTimeout(t0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // texto 12: "voltar" do Android na raiz de outra aba → o navegador das abas vai ao My Planner; marca para ele voltar ao dia de hoje.
  // Não consome o toque (devolve false): subpáginas e o próprio navegador continuam respondendo como antes.
  const caminhoRef = useRef(caminho); caminhoRef.current = caminho;
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    const s = BackHandler.addEventListener('hardwareBackPress', () => { if (caminhoRef.current !== '/') marcarVoltarAndroid(); return false; });
    return () => s.remove();
  }, []);

  const vencido = situacao('planner').tipo === 'vencida' && caminho !== '/ajustes' && caminho !== '/mensagens';
  return (
    <View style={{ flex: 1 }}>
    <Tabs tabBar={p => <Barra {...p} />} backBehavior="initialRoute" screenOptions={{ headerShown: false, sceneStyle: { backgroundColor: C.bg } }}>
      <Tabs.Screen name="mensagens" />
      <Tabs.Screen name="anotacoes" />
      <Tabs.Screen name="index" />
      <Tabs.Screen name="areas" />
      <Tabs.Screen name="ajustes" />
      <Tabs.Screen name="aba/[id]" />
    </Tabs>
    {vencido && <View style={StyleSheet.absoluteFill}><PlanoBasicoVencido onAjustes={() => router.navigate('/ajustes')} /></View>}
    </View>
  );
}
